import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { FileText, Download, Calendar, TrendingUp, DollarSign, HardHat, CalendarClock, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const StatRow = ({ label, value, bold, tone }) => (
  <div className={`flex items-center justify-between py-2 px-3 ${bold ? 'font-bold' : ''}`}>
    <span className={`text-sm ${bold ? 'text-slate-800' : 'text-slate-500'}`}>{label}</span>
    <span className={`text-sm tabular-nums ${tone || 'text-slate-800'}`}>{value}</span>
  </div>
);

export const MonthlyStatement = ({ mode = 'project', projectId, userId, projectName }) => {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (m) => {
    setLoading(true);
    setError(null);
    try {
      const res =
        mode === 'portfolio'
          ? await apiService.getPortfolioStatement(userId, m)
          : await apiService.getProjectStatement(projectId, m);
      setData(res.data);
      if (res.data.available_months?.length) setMonths(res.data.available_months);
      if (!m) setMonth(res.data.month);
    } catch (e) {
      setError('Could not load statement.');
    } finally {
      setLoading(false);
    }
  }, [mode, projectId, userId]);

  useEffect(() => { load(month || undefined); }, [load]); // initial load

  const onMonthChange = (m) => { setMonth(m); load(m); };

  const downloadPdf = () => {
    const url =
      mode === 'portfolio'
        ? apiService.portfolioStatementPdfUrl(userId, month)
        : apiService.projectStatementPdfUrl(projectId, month);
    window.open(url, '_blank');
    toast.success('Generating PDF…');
  };

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900" data-testid="statement-title">
            {mode === 'portfolio' ? 'Portfolio Monthly Statement' : 'Monthly Loan Statement'}
          </h3>
          <p className="text-xs text-slate-500">
            {mode === 'portfolio' ? 'Interest & activity across all your loans' : (projectName || 'Interest & draw activity')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Calendar className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
          <select
            data-testid="statement-month-select"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            className="appearance-none [-webkit-appearance:none] pl-9 pr-9 py-2 text-sm rounded-lg border border-slate-300 bg-white text-slate-800 focus:ring-2 focus:ring-slate-900 focus:outline-none"
          >
            {months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <button
          data-testid="statement-download-pdf"
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-700 transition-colors"
        >
          <Download className="w-4 h-4" /> PDF
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        {header}
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-slate-100 rounded-lg" />
          <div className="h-32 bg-slate-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        {header}
        <div className="text-center py-8">
          <p className="text-sm text-slate-500 mb-3">{error || 'No statement data.'}</p>
          <button onClick={() => load(month || undefined)} className="px-4 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'portfolio') {
    const t = data.totals || {};
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="portfolio-statement">
        {header}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi label="Interest accrued" value={money(t.interest_accrued_period)} tone="text-sky-700" />
          <Kpi label="Interest paid" value={money(t.interest_paid_period)} tone="text-emerald-700" />
          <Kpi label="Balance due" value={money(t.balance_due)} tone="text-red-600" highlight />
          <Kpi label="Draws funded" value={money(t.draws_total)} tone="text-slate-800" />
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Project</th>
                <th className="text-right px-3 py-2 font-semibold">Closing bal.</th>
                <th className="text-right px-3 py-2 font-semibold">Int. accrued</th>
                <th className="text-right px-3 py-2 font-semibold">Int. paid</th>
                <th className="text-right px-3 py-2 font-semibold">Balance due</th>
                <th className="text-right px-3 py-2 font-semibold">Draws</th>
              </tr>
            </thead>
            <tbody>
              {(data.projects || []).map((r, i) => (
                <tr key={r.project_id || i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-3 py-2 text-slate-800">{r.project_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.closing_balance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-sky-700">{money(r.interest_accrued_period)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{money(r.interest_paid_period)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-600">{money(r.balance_due)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.draws_total)}</td>
                </tr>
              ))}
              {(!data.projects || data.projects.length === 0) && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No loans with activity this period.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold">
                <td className="px-3 py-2">Portfolio total</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(t.closing_balance)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(t.interest_accrued_period)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(t.interest_paid_period)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-red-600">{money(t.balance_due)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(t.draws_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  // ── Per-project view ──
  const i = data.interest || {};
  const a = data.activity || {};
  const pp = i.projected_payoff;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="project-statement">
      {header}
      <p className="text-xs text-slate-400 mb-4">
        Period {formatDate(data.period_start)} → {formatDate(data.period_end)} · as of {formatDate(data.as_of)}
      </p>

      {/* Interest summary */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 text-white px-3 py-2 text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Interest Summary
          </div>
          <div className="divide-y divide-slate-100">
            <StatRow label="Opening balance (drawn)" value={money(i.opening_balance)} />
            <StatRow label="Closing balance (drawn)" value={money(i.closing_balance)} />
            <StatRow label={`Days accrued (Actual/${i.day_basis})`} value={i.days_accrued} />
            <StatRow label="Rate(s) in period" value={(i.rates_in_period || []).map((r) => `${r}%`).join(', ') || `${i.current_rate}%`} />
            <StatRow label="Interest accrued this period" value={money(i.interest_accrued_period)} tone="text-sky-700" />
            <StatRow label="Interest paid this period" value={money(i.interest_paid_period)} tone="text-emerald-700" />
            <StatRow label="Current per-diem" value={`${money(i.per_diem)}/day`} />
            <StatRow label="Balance due" value={money(i.balance_due)} bold tone="text-red-600" />
          </div>
        </div>

        {pp ? (
          <div className="rounded-lg border border-amber-200 overflow-hidden">
            <div className="bg-amber-600 text-white px-3 py-2 text-sm font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Projected Payoff at Maturity
            </div>
            <div className="divide-y divide-amber-100 bg-amber-50">
              <StatRow label="Maturity date" value={formatDate(pp.maturity_date)} />
              <StatRow label="Days to maturity" value={pp.days_to_maturity} />
              <StatRow label="Additional interest to maturity" value={money(pp.projected_additional_interest)} />
              <StatRow label="Projected total interest" value={money(pp.projected_total_interest)} />
              <StatRow label="Projected interest due at payoff" value={money(pp.projected_interest_due)} bold tone="text-amber-700" />
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 p-4 flex flex-col items-center justify-center text-center">
            <Calendar className="w-6 h-6 text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Set a <span className="font-semibold">Loan Maturity Date</span> in Edit Loan Details to see the projected payoff.</p>
          </div>
        )}
      </div>

      {/* Activity sections */}
      <ActivityTable
        icon={<DollarSign className="w-4 h-4" />}
        title="Draws Funded"
        rows={a.draws}
        cols={['Date', 'Description', 'Amount']}
        render={(d) => [formatDate(d.date), d.description, money(d.amount)]}
        total={a.draws?.length ? ['', 'Total draws', money(a.draws_total)] : null}
        testid="stmt-draws"
      />
      <ActivityTable
        icon={<HardHat className="w-4 h-4" />}
        title="Payments to Subs & Vendors"
        rows={a.payments}
        cols={['Date', 'Payee', 'Phase', 'Perfected', 'Amount']}
        render={(p) => [formatDate(p.date), p.payee, p.phase, p.perfected ? 'Yes' : 'No', money(p.amount)]}
        total={a.payments?.length ? ['', '', '', 'Total', money(a.payments_total)] : null}
        testid="stmt-payments"
      />
      <ActivityTable
        icon={<HardHat className="w-4 h-4" />}
        title="Work Completed"
        rows={a.work_completed}
        cols={['Completed', 'Phase', 'Category', 'Cost']}
        render={(w) => [formatDate(w.completed_date), w.phase, w.category, money(w.cost)]}
        total={a.work_completed?.length ? ['', '', 'Total', money(a.work_completed_total)] : null}
        testid="stmt-work"
      />
      <ActivityTable
        icon={<CalendarClock className="w-4 h-4" />}
        title="Upcoming Schedule (expected end dates)"
        rows={a.upcoming}
        cols={['Expected End', 'Phase', 'Status', '']}
        render={(u) => [formatDate(u.scheduled_end), u.phase, u.status, u.overdue ? 'OVERDUE' : '']}
        testid="stmt-upcoming"
      />
    </div>
  );
};

const Kpi = ({ label, value, tone, highlight }) => (
  <div className={`rounded-lg p-3 border ${highlight ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const ActivityTable = ({ icon, title, rows, cols, render, total, testid }) => (
  <div className="mb-4" data-testid={testid}>
    <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">{icon} {title}</h4>
    {(!rows || rows.length === 0) ? (
      <p className="text-xs text-slate-400 px-1">None in this period.</p>
    ) : (
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>{cols.map((c, i) => (
              <th key={i} className={`px-3 py-2 font-semibold text-slate-600 ${i === cols.length - 1 ? 'text-right' : 'text-left'}`}>{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => {
              const cells = render(r);
              return (
                <tr key={ri} className={ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                  {cells.map((cell, ci) => (
                    <td key={ci} className={`px-3 py-2 ${ci === cells.length - 1 ? 'text-right tabular-nums' : 'text-slate-700'} ${cell === 'OVERDUE' ? 'text-red-600 font-semibold' : ''}`}>{cell}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {total && (
            <tfoot><tr className="bg-slate-100 font-semibold">
              {total.map((cell, ci) => (
                <td key={ci} className={`px-3 py-2 ${ci === total.length - 1 ? 'text-right tabular-nums' : ''}`}>{cell}</td>
              ))}
            </tr></tfoot>
          )}
        </table>
      </div>
    )}
  </div>
);

export default MonthlyStatement;
